/**
 * `neutral` reports a change the user just made and offers to reverse it ("Button1 deleted · Undo"): no
 * icon, no title — it is not news that something succeeded, only a way back.
 */
export type ToastType = "error" | "success" | "warning" | "info" | "loading" | "neutral";

export type ToastId = string;

/** How long a toast of each kind stays up. A loading toast stays until it is updated or dismissed. */
export const TOAST_DURATION_MS: number = 5000;
export const TOAST_ERROR_DURATION_MS: number = 7000;
export const TOAST_LOADING_DURATION_MS: number = Infinity;

/** A button on a toast — the Cancel of a long-running promise toast. Clicking it closes the toast. */
export interface IToastAction {
  label: string;
  onClick: () => void;
  testId?: string;
}

export interface ToastEvent {
  id: ToastId;
  type: ToastType;
  message: string;
  title?: string;
  /** Milliseconds; `Infinity` keeps the toast up until it is updated or dismissed. */
  duration: number;
  /** A button under the message; absent on most toasts. */
  action?: IToastAction;
}

/**
 * A change to a toast that is already up. Changing `type` without `duration` takes that type's
 * duration. `action: undefined`, given explicitly, takes the toast's button away.
 */
export interface IToastPatch {
  type?: ToastType;
  message?: string;
  title?: string;
  duration?: number;
  action?: IToastAction;
}

export interface IToastPromiseMessages<T> {
  loading: string;
  success: string | ((value: T) => string);
  /** A fixed text, or one worked out from the rejection — `failureMessageOr(fallback)` for the usual one. */
  error: string | ((reason: unknown) => string);
}

export interface IToastPromiseOptions {
  /**
   * A button on the loading toast — Cancel for work the user can stop (a buffered export). Clicking
   * it runs `onClick` (the owner aborts its work there) and closes the toast; whatever the promise
   * does afterwards is neither shown nor logged. The button goes away when the promise settles.
   */
  action?: IToastAction;
}

export interface IToastShowAction {
  kind: "show";
  toast: ToastEvent;
}

export interface IToastUpdateAction {
  kind: "update";
  id: ToastId;
  patch: IToastPatch;
}

export interface IToastDismissAction {
  kind: "dismiss";
  id: ToastId;
}

export type ToastAction = IToastShowAction | IToastUpdateAction | IToastDismissAction;

type ToastListener = (action: ToastAction) => void;

export const toastDurationFor = (type: ToastType): number => {
  if (type === "loading") return TOAST_LOADING_DURATION_MS;
  if (type === "error") return TOAST_ERROR_DURATION_MS;
  return TOAST_DURATION_MS;
};

const resolveMessage = <V>(message: string | ((value: V) => string), value: V): string =>
  typeof message === "function" ? message(value) : message;

interface IFailureBody {
  message?: unknown;
}

interface IFailureResponse {
  data?: IFailureBody | null;
}

/** The part of a rejection the backend fills in: an Axios error's `response.data.message`. */
interface IFailureLike {
  response?: IFailureResponse | null;
}

/**
 * What a failure says to the user: the backend's own message when the rejection carries one (an
 * Axios error's `response.data.message`), else the error's own message, else `fallback`. Read
 * directly rather than through `KMainFunctions.getApiErrorMessage`, which imports this module.
 */
export const getFailureMessage = (reason: unknown, fallback: string): string => {
  const failure: IFailureLike | null = typeof reason === "object" ? (reason as IFailureLike | null) : null;
  const apiMessage: unknown = failure?.response?.data?.message;
  if (typeof apiMessage === "string" && apiMessage.length > 0) {
    return apiMessage;
  }
  if (reason instanceof Error && reason.message.length > 0) {
    return reason.message;
  }
  return fallback;
};

/**
 * `getFailureMessage` with its fallback bound, for a promise toast's `error`:
 * `toastService.promise(work, { loading, success, error: failureMessageOr("The export failed.") })`.
 */
export const failureMessageOr =
  (fallback: string): ((reason: unknown) => string) =>
  (reason: unknown): string =>
    getFailureMessage(reason, fallback);

/**
 * The designer's one toast channel. Every call returns the toast's id, which is also its identity
 * on screen: `update` changes that toast in place (a loading toast turning into its result) and
 * `dismiss` closes it. The provider (`KuikaToastProvider`) is the only subscriber.
 */
class ToastService {
  private listeners: Set<ToastListener> = new Set();

  subscribe(listener: ToastListener): () => void {
    this.listeners.add(listener);
    return (): void => this.unsubscribe(listener);
  }

  unsubscribe(listener: ToastListener): void {
    this.listeners.delete(listener);
  }

  private emit(action: ToastAction): void {
    this.listeners.forEach((listener: ToastListener): void => listener(action));
  }

  private generateId(): ToastId {
    return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  private show(type: ToastType, message: string, title?: string, action?: IToastAction): ToastId {
    const id: ToastId = this.generateId();
    const event: ToastEvent = { id, type, message, title, duration: toastDurationFor(type) };
    this.emit({ kind: "show", toast: action === undefined ? event : { ...event, action } });
    return id;
  }

  private logError(message: string, title?: string): void {
    console.error(`[Toast Error] ${title ?? "Something went wrong"}:`, message);
  }

  success(message: string, title?: string): ToastId {
    return this.show("success", message, title);
  }

  /**
   * A neutral toast carrying one button — the Undo of a destructive action that already happened
   * ("Button1 deleted · Undo"). No icon and no title. Clicking the button runs `action.onClick` and
   * closes the toast.
   */
  neutralWithAction(message: string, action: IToastAction): ToastId {
    return this.show("neutral", message, undefined, action);
  }

  info(message: string, title?: string): ToastId {
    return this.show("info", message, title);
  }

  warning(message: string, title?: string): ToastId {
    return this.show("warning", message, title);
  }

  error(message: string, title?: string): ToastId {
    this.logError(message, title);
    return this.show("error", message, title);
  }

  /**
   * Changes a toast that is still up and restarts its timer. A toast that has been dismissed or has
   * timed out stays gone — a late result never brings it back.
   */
  update(id: ToastId, patch: IToastPatch): void {
    const duration: number | undefined =
      patch.duration ?? (patch.type !== undefined ? toastDurationFor(patch.type) : undefined);
    this.emit({ kind: "update", id, patch: duration === undefined ? patch : { ...patch, duration } });
  }

  dismiss(id: ToastId): void {
    this.emit({ kind: "dismiss", id });
  }

  /**
   * Shows a loading toast that turns into the promise's result in the same card, then times out
   * like any other toast. Returns the id: an owner unmounting before the promise settles should
   * `dismiss(id)` rather than leave a loading toast behind. `options.action` puts a button (Cancel)
   * on the loading toast — see `IToastPromiseOptions`.
   */
  promise<T>(promise: Promise<T>, messages: IToastPromiseMessages<T>, options?: IToastPromiseOptions): ToastId {
    const ownerAction: IToastAction | undefined = options?.action;
    let isCancelled: boolean = false;
    const action: IToastAction | undefined =
      ownerAction === undefined
        ? undefined
        : {
            ...ownerAction,
            onClick: (): void => {
              isCancelled = true;
              ownerAction.onClick();
            }
          };
    // A settled toast has nothing left to cancel; a toast without an action keeps its old patch.
    const settledPatch: IToastPatch = action === undefined ? {} : { action: undefined };

    const id: ToastId = this.show("loading", messages.loading, undefined, action);
    promise.then(
      (value: T): void => {
        if (isCancelled) return;
        this.update(id, { ...settledPatch, type: "success", message: resolveMessage(messages.success, value) });
      },
      (reason: unknown): void => {
        if (isCancelled) return;
        const message: string = resolveMessage(messages.error, reason);
        this.logError(message);
        this.update(id, { ...settledPatch, type: "error", message });
      }
    );
    return id;
  }
}

export const toastService: ToastService = new ToastService();

/** Function-style access to the same channel, for call sites that import `toast`. */
export interface IToastApi {
  success(message: string, title?: string): ToastId;
  neutralWithAction(message: string, action: IToastAction): ToastId;
  info(message: string, title?: string): ToastId;
  warning(message: string, title?: string): ToastId;
  error(message: string, title?: string): ToastId;
  update(id: ToastId, patch: IToastPatch): void;
  dismiss(id: ToastId): void;
  promise<T>(promise: Promise<T>, messages: IToastPromiseMessages<T>, options?: IToastPromiseOptions): ToastId;
}

export const toast: IToastApi = {
  success: (message: string, title?: string): ToastId => toastService.success(message, title),
  neutralWithAction: (message: string, action: IToastAction): ToastId =>
    toastService.neutralWithAction(message, action),
  info: (message: string, title?: string): ToastId => toastService.info(message, title),
  warning: (message: string, title?: string): ToastId => toastService.warning(message, title),
  error: (message: string, title?: string): ToastId => toastService.error(message, title),
  update: (id: ToastId, patch: IToastPatch): void => toastService.update(id, patch),
  dismiss: (id: ToastId): void => toastService.dismiss(id),
  promise: <T>(promise: Promise<T>, messages: IToastPromiseMessages<T>, options?: IToastPromiseOptions): ToastId =>
    toastService.promise(promise, messages, options)
};
